'use client'

import { clearSessionAction } from '@/app/actions/session'
import { AUTH_USER_COOKIE } from '@/lib/auth-constants'
import {
  LAYOUT_LAST_ACTIVE_KEY,
  LAYOUT_STORAGE_KEY,
} from '@/components/editor/dock/layout-model'
import { DETACHED_TAB_STORAGE_KEY } from '@/hooks/use-detachable-tab'

const PENDING_AUTH_KEY = 'pending_auth_session'

/** Explicit keys written by auth, dashboard, and editor flows. */
const LOCAL_STORAGE_KEYS = [
  'auth_user',
  'auth_profile',
  'rudrise-project-name',
  'rudrise-project-id',
  'rudrise-cloud-project-id',
  'rudrise-session-id-v1',
  'rudrise-auth-compiler-session',
  'rudrise-compiler-state-v1',
  LAYOUT_STORAGE_KEY,
  DETACHED_TAB_STORAGE_KEY,
  'layout-explorer-bottom',
  'layout-sidebar',
] as const

const SESSION_STORAGE_KEYS = [
  PENDING_AUTH_KEY,
  LAYOUT_STORAGE_KEY,
  LAYOUT_LAST_ACTIVE_KEY,
  'rudrise-auth-compiler-session',
] as const

const LOCAL_STORAGE_PREFIXES = ['rudrise-', 'auth_'] as const
const SESSION_STORAGE_PREFIXES = ['rudrise-'] as const

function removeStorageKeys(storage: Storage, keys: readonly string[]): void {
  for (const key of keys) {
    try {
      storage.removeItem(key)
    } catch {
      // ignore quota / private-mode errors
    }
  }
}

function removeStorageKeysWithPrefixes(storage: Storage, prefixes: readonly string[]): void {
  const keysToRemove: string[] = []
  for (let i = 0; i < storage.length; i += 1) {
    const key = storage.key(i)
    if (key && prefixes.some((prefix) => key.startsWith(prefix))) {
      keysToRemove.push(key)
    }
  }
  for (const key of keysToRemove) {
    try {
      storage.removeItem(key)
    } catch {
      // ignore
    }
  }
}

/** Clear readable auth_user cookie immediately (httpOnly tokens cleared by /api/auth/logout). */
export function clearReadableAuthCookie(): void {
  if (typeof document === 'undefined') return
  const secure = window.location.protocol === 'https:' ? '; Secure' : ''
  document.cookie = `${AUTH_USER_COOKIE}=; path=/; max-age=0; SameSite=Lax${secure}`
}

/** Remove auth, project, editor session, and layout data from browser storage. */
export function clearClientSessionStorage(): void {
  if (typeof window === 'undefined') return

  removeStorageKeys(localStorage, LOCAL_STORAGE_KEYS)
  removeStorageKeys(sessionStorage, SESSION_STORAGE_KEYS)
  removeStorageKeysWithPrefixes(localStorage, LOCAL_STORAGE_PREFIXES)
  removeStorageKeysWithPrefixes(sessionStorage, SESSION_STORAGE_PREFIXES)
}

/** Full client-side logout cleanup (storage + editor session cookie). */
export async function performClientLogoutCleanup(): Promise<void> {
  clearClientSessionStorage()
  clearReadableAuthCookie()

  try {
    await clearSessionAction()
  } catch {
    // Server may already have cleared the cookie via /api/auth/logout.
  }
}
